import { useCallback, useRef } from "react";
import { useMutation } from "@edreamsodigeo/frontend-api-client";

import { MEMBERSHIP_PRODUCT_EVALUATION_MUTATION } from "~/clientState/providers/operations/membershipProductEvaluation";
import type {
  MutationMembershipProductEvaluationArgs,
  UpdateMembershipProductEvaluationMutation,
  UpdateMembershipProductEvaluationMutationVariables,
} from "~/generated/graphql";
import { MembershipProductEvaluationCode } from "~/generated/graphql";
import { useShoppingInfo } from "./useShoppingInfo";

interface UseMutationType {
  code: MembershipProductEvaluationCode;
  mutate: (variables: MembershipProductEvaluationMutationVariables) => void;
  loading: boolean;
}

export interface MembershipProductEvaluationMutationVariables {
  paymentInstrumentToken: string;
  creditCardBin: string;
  creditCardDate: string;
}

export const useMembershipProductEvaluationMutation = (): UseMutationType => {
  const { data, loading, mutate, errors } = useMutation<
    UpdateMembershipProductEvaluationMutation,
    UpdateMembershipProductEvaluationMutationVariables
  >(MEMBERSHIP_PRODUCT_EVALUATION_MUTATION);

  const code = useRef(MembershipProductEvaluationCode.NoRisk);

  const shoppingInfo = useShoppingInfo();
  const membershipProductEvaluationMutate = useCallback(
    (mutationVariables: MembershipProductEvaluationMutationVariables): void => {
      const request: MutationMembershipProductEvaluationArgs = {
        request: {
          ...mutationVariables,
          shoppingInfo,
        },
      };
      mutate(request);
    },
    [mutate, shoppingInfo],
  );

  if (!loading && errors) {
    code.current = MembershipProductEvaluationCode.NoRisk;
  }

  if (!loading && data?.membershipProductEvaluation?.code) {
    code.current = data.membershipProductEvaluation.code;
  }

  return {
    code: code.current,
    mutate: membershipProductEvaluationMutate,
    loading,
  };
};
